'use client';

import { useAuthContext } from './use-auth-context';

// ----------------------------------------------------------------------

/**
 * Phase 24 — permission-check hooks.
 *
 * The backend resolves permissions[] from the `role_permissions` table at
 * token-issuance time and embeds the codes in the JWT. Components should
 * use these hooks instead of comparing role strings, so adding a new role
 * (e.g. `support_lead`) does not require touching every UI gate.
 *
 * Permission codes are defined server-side in
 * `backend/internal/auth/permissions.go` and seeded by migration 00054.
 * Pass codes verbatim — there's no enum on the panel side intentionally
 * (the backend catalog is the source of truth).
 */

/** Returns `true` when the current user has the given permission code. */
export function useHasPermission(perm: string): boolean {
  const { permissions } = useAuthContext();
  return permissions.includes(perm);
}

/**
 * Returns `true` when the current user has at least one of the given
 * permission codes (OR semantics). Useful for UI bits gated by either
 * an `*.org` or `*.own`-scoped variant of the same action.
 */
export function useHasAnyPermission(...perms: string[]): boolean {
  const { permissions } = useAuthContext();
  if (perms.length === 0) return true;
  return perms.some((p) => permissions.includes(p));
}

/**
 * Returns `true` only when the current user has every permission in the
 * given list (AND semantics).
 */
export function useHasAllPermissions(...perms: string[]): boolean {
  const { permissions } = useAuthContext();
  if (perms.length === 0) return true;
  return perms.every((p) => permissions.includes(p));
}
